import { useFormik } from 'formik'
import { ChangeEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../components/button'
import { BackButton } from '../../components/button/backButton'
import { ActionChip } from '../../components/cards/actionChip'
import { CategoryCard } from '../../components/cards/categoryCard'
import { CategorySectionSummary } from '../../components/common/category-section-summary'
import Icon from '../../components/icon'
import { ImageBanner } from '../../components/images/imageBanner'
import { EditInput } from '../../components/inputs/editInput'
import { TextInput } from '../../components/inputs/textInput'
import { ImageUploadPreviewModal } from '../../components/modal/app_modals/ImagePreviewModal'
import { SelectCategoriesModal } from '../../components/modal/app_modals/selectCategoriesModal'
import { SelectFulfilmentCenterModal } from '../../components/modal/app_modals/selectFulfilmentCenter'
import { SelectLocationModal } from '../../components/modal/app_modals/selectLocationModal'
import { SelectTagsModal } from '../../components/modal/app_modals/selectTagsModal'
import Spinner from '../../components/spinner/Spinner'
import CustSwitch from '../../components/switch'
import { TextSubtext } from '../../components/typography/textSubtext'
import { removeElementAtIndex } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import {
  useCreateListArticleMutation,
  useEditListArticleMutation,
  useFetchSingleListArticleQuery,
} from '../../redux-toolkits/lists/list.slice'
import { ArticleLocation } from '../../redux-toolkits/lists/list.types'
import { ListCategoryDoc } from '../../redux-toolkits/categories/categories.type'
import { Tag } from '../../redux-toolkits/products/products.type'
import { useFetchDeliveryRegionsQuery } from '../../redux-toolkits/user/user.slice'
import { useGetFulfilmentCentersQuery } from '../../redux-toolkits/vendors/vendors.slice'
import { FulfilmentCentre } from '../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { CreateArticle, Sections } from '../../redux-toolkits/lists/list.types'
import { CreateArticleSchema } from '../../validation/validate'
import { ArticleSectionEditView } from './components/articleSectionEditView'
import { ArticleSectionLiveView } from './components/articleSectionLiveView'
import { ArticleInterests } from './article-interests'
import { ArticleAudienceFields } from './article-audience-fields'
import { toast } from 'react-toastify'

export const SingleListArticle = ({
  mode = 'create',
}: {
  mode?: 'create' | 'edit'
}) => {
  const [uploadedImage, setUploadedImage] = useState<File>()
  const [sections, setSections] = useState<Sections>([])
  const [activeSectionIndex, setActiveSectionIndex] = useState<number | null>(
    null,
  )

  // const [modal, setModal] = useState<string>('')
  // const [modalOpen, setModalOpen] = useState<boolean>(false)
  const { handleModalOpen, setModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl()
  const [locations, setLocations] = useState<ArticleLocation[]>([])
  const [hasPrefilledLocations, setHasPrefilledLocations] = useState(false)
  const params = useParams<{ id: string }>()

  const navigate = useNavigate()

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }

    const selectedFile = event.target.files[0]
    setUploadedImage(selectedFile)

    handleModalOpen('imageUploadPreviewModal')
  }

  const {
    touched,
    errors,
    handleChange,
    handleBlur,
    values,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues: {
      title: '',
      description: '',
      bannerUrl: '',
      tags: [],
      interests: [],
      ageRange: null,
      genders: [],
      category: undefined,
      addAllToCart: false,
      fulfilmentCentre: [],
    } as {
      title: string
      description: string
      bannerUrl: string
      tags: Tag[]
      interests: string[]
      ageRange: { min?: number; max?: number } | null
      genders: string[]
      category?: ListCategoryDoc
      addAllToCart: boolean
      fulfilmentCentre: FulfilmentCentre[]
    },
    validationSchema: CreateArticleSchema,
    onSubmit: (values) => {
      const { min, max } = values.ageRange ?? {}
      if (min !== undefined && max !== undefined && min > max) {
        toast.error('Minimum age cannot exceed maximum age.')
        return
      }
      const payload: CreateArticle = {
        ...values,
        ageRange:
          min === undefined && max === undefined ? null : values.ageRange,
        tags: values.tags.map((tag) => tag._id),
        category: values?.category?._id,
        location: locations.map((loc) => loc._id),
        fulfilmentCentre: values.fulfilmentCentre[0]?._id,
        article: sections.map((section) => {
          return {
            ...section,
            content: section?.content?.map((content) => {
              return {
                ...content,
                products: content.products.map((product) => {
                  return product._id ?? product
                }),
              }
            }),
          }
        }),
      }

      if (mode === 'create') {
        createListArticle(payload)?.then((resp) => {
          navigate(`/lists/articles/view/${resp.data?.data.article._id}`)
        })
      }

      if (mode === 'edit') {
        editListArticle({ id: params.id ?? '', data: { article: payload } })
      }
    },
  })

  const { data: singleArticleData, isFetching } =
    useFetchSingleListArticleQuery(
      { id: params.id ?? '' },
      {
        refetchOnMountOrArgChange: true,
        skip: mode === 'create',
      },
    )

  const { data: availableLocationsData, isFetching: fetchingLocations } =
    useFetchDeliveryRegionsQuery(
      { page: 1, limit: 1000, isAvailable: true },
      { refetchOnMountOrArgChange: true },
    )
  const regionById = new Map(
    (availableLocationsData?.data.docs ?? []).map((region) => [
      region._id,
      { ...region, locationType: 'delivery' as const },
    ]),
  )

  const { data: fulfilmentCentresData, isFetching: fetchingFulfilmentCentres } =
    useGetFulfilmentCentersQuery({ page: 1, limit: 1000 })
  const fulfilmentCentreById = new Map(
    (fulfilmentCentresData?.data.docs ?? []).map((centre) => [
      centre._id,
      { ...centre, locationType: 'pickup' as const },
    ]),
  )
  const normalizeArticleLocations = (
    articleLocations?: Array<ArticleLocation | string>,
  ) =>
    (articleLocations ?? [])
      .map((location) => {
        if (typeof location !== 'string') {
          return {
            ...location,
            locationType:
              location.locationType ??
              (location.address ? ('pickup' as const) : ('delivery' as const)),
          }
        }

        return (
          regionById.get(location) ??
          fulfilmentCentreById.get(location) ??
          ({
            _id: location,
            name: location,
            locationType: 'delivery',
          } as ArticleLocation)
        )
      })
      .filter((location) => location?._id)

  const [createListArticle, { isLoading: isCreating }] =
    useCreateListArticleMutation()

  const [editListArticle, { isLoading: isEditing }] =
    useEditListArticleMutation()

  useEffect(() => {
    if (singleArticleData) {
      setFieldValue('title', singleArticleData?.data.article.title)
      setFieldValue('description', singleArticleData?.data.article.description)
      setFieldValue('bannerUrl', singleArticleData?.data.article.bannerUrl)
      setFieldValue('tags', singleArticleData?.data.tags)
      setFieldValue(
        'interests',
        singleArticleData?.data.article.interests ?? [],
      )
      setFieldValue(
        'ageRange',
        singleArticleData?.data.article.ageRange ?? null,
      )
      setFieldValue('genders', singleArticleData?.data.article.genders ?? [])
      setFieldValue('category', singleArticleData?.data.article.category)
      setFieldValue(
        'addAllToCart',
        singleArticleData?.data.article.addAllToCart ?? false,
      )
      setSections(singleArticleData?.data.article.article)
      setLocations(normalizeArticleLocations(singleArticleData.data.location))
      const articleFcId = singleArticleData.data.article.fulfilmentCentre
      const matchedFc = (fulfilmentCentresData?.data.docs ?? []).find(
        (fc) => fc._id === articleFcId,
      )
      setFieldValue('fulfilmentCentre', matchedFc ? [matchedFc] : [])
    }
  }, [singleArticleData, availableLocationsData, fulfilmentCentresData])

  useEffect(() => {
    if (mode !== 'create' || hasPrefilledLocations) {
      return
    }

    const availableLocations = (availableLocationsData?.data.docs ?? [])
      .filter((location) => !location.parentRegion)
      .map((location) => ({
        ...location,
        locationType: 'delivery' as const,
      }))

    if (availableLocations.length < 1) {
      return
    }

    setLocations(availableLocations)
    setHasPrefilledLocations(true)
  }, [availableLocationsData, hasPrefilledLocations, mode])

  return (
    <Layout>
      {mode === 'edit' && isFetching ? (
        <div className="flex items-center justify-center py-20">
          <Spinner fullScreen={false} />
        </div>
      ) : (
        <div className="flex flex-col gap-0">
          <div className="flex items-center justify-between mb-8">
            <BackButton />
            <Button
              label={mode === 'create' ? 'Save' : 'Save Changes'}
              type="button"
              onClick={() => {
                handleSubmit()
              }}
              loading={isCreating || isEditing}
              disabled={isCreating || isEditing}
              primary
              size="medium"
            />
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-8">
            {/* Banner */}
            <ImageBanner
              imageUrl={values.bannerUrl}
              loading={false}
              className="!h-[200px] md:!h-[320px] lg:!h-[380px]"
              onImageEdit={(e) => {
                handleFileChange(e)
              }}
              editIconPos="top-right"
            />

            {/* Title */}
            <TextInput
              type="text"
              name="title"
              onChange={handleChange}
              value={values.title}
              onBlur={handleBlur}
              errors={errors.title && touched.title ? errors.title : ''}
              inputClass="!text-4xl font-recoleta font-semibold leading-tight"
              className="!border-x-0 !border-t-0 !border-b !border-divider-gray !rounded-none"
              placeholder="Good List title…"
              hasIcon={false}
            />

            {/* Description */}
            <TextInput
              type="textarea"
              name="description"
              onChange={handleChange}
              value={values.description}
              onBlur={handleBlur}
              errors={
                errors.description && touched.description
                  ? errors.description
                  : ''
              }
              inputClass="!text-lg !text-sec-black !leading-relaxed"
              className="!border-x-0 !border-t-0 !border-b !border-divider-gray !rounded-none"
              hasIcon={false}
              placeholder="Write a short description of this good list…"
              rows={4}
            />

            {/* Metadata row */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col divide-y divide-divider-gray border-divider-gray">
                {/* Locations */}
                <div className="py-4">
                  <EditInput
                    title="Available Locations"
                    onEditClick={() => handleModalOpen('selectLocationModal')}
                    onCancel={() => {}}
                    onEditSave={() => {}}
                    editing={false}
                  >
                    {() =>
                      locations.length < 1 ? (
                        <p className="text-sm text-sec-black">
                          {fetchingLocations || fetchingFulfilmentCentres
                            ? 'Loading locations…'
                            : 'No locations added'}
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {locations.map((loc, idx) => (
                            <ActionChip
                              key={loc._id}
                              name={loc.name}
                              onDismiss={() => {
                                const locationsCopy: ArticleLocation[] =
                                  JSON.parse(JSON.stringify(locations))
                                setLocations(
                                  removeElementAtIndex(locationsCopy, idx),
                                )
                              }}
                            />
                          ))}
                        </div>
                      )
                    }
                  </EditInput>
                </div>

                {/* Tags */}
                <div className="py-4">
                  <EditInput
                    title="Tags"
                    onEditClick={() => handleModalOpen('selectTagsModal')}
                    onCancel={() => {}}
                    onEditSave={() => {}}
                    editing={false}
                  >
                    {() =>
                      values.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {values.tags.map((tag: Tag, idx) => (
                            <ActionChip key={`tag-${idx}`} name={tag.name} />
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-sec-black">No tags added</p>
                      )
                    }
                  </EditInput>
                </div>

                <div className="space-y-3 py-4">
                  <h2 className="font-medium">Shopping interests</h2>
                  <ArticleInterests
                    selectedIds={values.interests}
                    onChange={(ids) => {
                      void setFieldValue('interests', ids)
                    }}
                  />
                </div>

                <ArticleAudienceFields
                  ageRange={values.ageRange}
                  genders={values.genders}
                  onAgeChange={(range) => {
                    void setFieldValue('ageRange', range)
                  }}
                  onGendersChange={(genders) => {
                    void setFieldValue('genders', genders)
                  }}
                />

                {/* Category */}
                <div className="py-4">
                  <EditInput
                    title="Category"
                    onEditClick={() => handleModalOpen('selectCategoriesModal')}
                    onCancel={() => {}}
                    onEditSave={() => {}}
                    editing={false}
                  >
                    {() => (
                      <>
                        {values?.category?.name ? (
                          <CategoryCard
                            category={values.category}
                            showIcon={false}
                          />
                        ) : (
                          <p className="text-sm text-sec-black">
                            No category selected
                          </p>
                        )}
                        <CategorySectionSummary
                          label="Good List section"
                          categories={values.category ? [values.category] : []}
                        />
                      </>
                    )}
                  </EditInput>
                </div>

                {/* Fulfilment Centres */}
                <div className="py-4">
                  <EditInput
                    title="Fulfilment Centre"
                    onEditClick={() =>
                      handleModalOpen('selectFulfilmentCenterModal')
                    }
                    onCancel={() => {}}
                    onEditSave={() => {}}
                    editing={false}
                    errors={
                      touched.fulfilmentCentre && errors.fulfilmentCentre
                        ? (errors.fulfilmentCentre as string)
                        : undefined
                    }
                  >
                    {() =>
                      values.fulfilmentCentre.length < 1 ? (
                        <p className="text-sm text-sec-black">
                          No fulfilment centre selected
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {values.fulfilmentCentre.map((fc, idx) => (
                            <ActionChip
                              key={fc._id}
                              name={fc.name}
                              onDismiss={() => {
                                setFieldValue(
                                  'fulfilmentCentre',
                                  removeElementAtIndex(
                                    [...values.fulfilmentCentre],
                                    idx,
                                  ),
                                )
                              }}
                            />
                          ))}
                        </div>
                      )
                    }
                  </EditInput>
                </div>

                {/* Add all to cart */}

                <div className="py-4 flex items-center justify-between">
                  <TextSubtext
                    text="Add all to cart"
                    subtext={`Show an "Add all to cart" button on this good list`}
                  />

                  <CustSwitch
                    checked={values.addAllToCart}
                    onChange={(checked) =>
                      setFieldValue('addAllToCart', checked)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Content sections */}
            <div className="flex flex-col gap-4">
              <p className="text-xs font-medium uppercase tracking-widest text-sec-black">
                Content Sections
              </p>

              {sections?.map((section, idx) => {
                const key = `article-section-${idx}`

                return activeSectionIndex === idx ? (
                  <ArticleSectionEditView
                    section={section}
                    setActiveSectionIndex={setActiveSectionIndex}
                    sections={sections}
                    setSections={setSections}
                    sectionIndex={idx}
                    key={key}
                  />
                ) : (
                  <ArticleSectionLiveView
                    section={section}
                    sections={sections}
                    setSections={setSections}
                    setActiveSectionIndex={setActiveSectionIndex}
                    sectionIndex={idx}
                    isAnyOtherSectionActive={
                      activeSectionIndex !== null && activeSectionIndex !== idx
                    }
                    products={singleArticleData?.data.products}
                    key={key}
                  />
                )
              })}

              <button
                type="button"
                onClick={() => {
                  const newSections = [...sections, { content: [] }]
                  if (sections.length === 0) {
                    setSections(newSections)
                    setActiveSectionIndex(newSections.length - 1)
                  }
                  if (
                    sections.length > 0 &&
                    (sections[sections.length - 1]?.content?.length ?? 0) > 0
                  ) {
                    setSections(newSections)
                    setActiveSectionIndex(newSections.length - 1)
                  }
                }}
                disabled={
                  sections.length === 1 &&
                  (sections[0]?.content?.length ?? 0) === 0
                }
                className="
                  w-full border-2 border-dashed border-divider-gray rounded-md py-4
                  flex items-center justify-center gap-2
                  text-sm text-sec-black
                  hover:border-black hover:text-black
                  transition-colors duration-200
                  disabled:opacity-40 disabled:cursor-not-allowed
                  disabled:hover:border-divider-gray disabled:hover:text-sec-black
                "
              >
                <Icon id="plus" width={16} height={16} />
                Add new section
              </button>
            </div>
          </form>

          <SelectTagsModal
            open={modalOpen && modal === 'selectTagsModal'}
            setOpen={setModalOpen}
            onCloseCallback={() => {
              handleModalClose()
            }}
            onSuccessCallback={(tags) => {
              setFieldValue('tags', tags)
              handleModalClose()
            }}
            selectedTags={values.tags}
          />

          {uploadedImage && (
            <ImageUploadPreviewModal
              image={uploadedImage}
              open={modal === 'imageUploadPreviewModal' && modalOpen}
              setOpen={setModalOpen}
              onCloseCallback={() => {
                setUploadedImage(undefined)
              }}
              uploadMode={'banner'}
              onSuccessCallback={(url) => {
                setFieldValue('bannerUrl', url)
              }}
            />
          )}

          <SelectLocationModal
            open={modal === 'selectLocationModal' && modalOpen}
            setOpen={setModalOpen}
            onCloseCallback={() => {}}
            onSuccessCallback={(selectedLocations) => {
              setLocations([...selectedLocations])
              handleModalClose()
            }}
            selectedLocations={locations}
          />

          <SelectFulfilmentCenterModal
            physicalOnly={false}
            open={modal === 'selectFulfilmentCenterModal' && modalOpen}
            setOpen={setModalOpen}
            onCloseCallback={() => handleModalClose()}
            onSuccessCallback={(centers, resetForm) => {
              setFieldValue('fulfilmentCentre', centers)
              resetForm()
              handleModalClose()
            }}
            selectedCenters={values.fulfilmentCentre}
            maxSelection={1}
          />
        </div>
      )}

      <SelectCategoriesModal
        open={modalOpen && modal === 'selectCategoriesModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={(categories) => {
          if (categories.length > 0) {
            setFieldValue('category', categories[0])
          }
          handleModalClose()
        }}
        selectedCategories={
          values.category ? [values.category as ListCategoryDoc] : undefined
        }
        maxSelection={1}
      />
    </Layout>
  )
}
